import React, { useMemo, useState } from 'react';
import {
  AuditTrail, ColumnPicker, ConfirmDialog, DataTable, DensityToggle, EmptyState, exportFilename,
  exportRows, FilterBar, Pagination, SearchInput, SideDrawer, Toolbar,
  useTableDensity, useToast,
  type ActiveFilter, type Column, type SortState,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  BLOCKED_LOCAL_PARTS, DISABLE_POLICY_EFFECT, DISABLE_POLICY_LABEL, DOMAINS,
  MEMBER_TYPE_DISCOUNT, MEMBER_TYPE_LABEL, domainAudit,
  type DisablePolicy, type EmailDomain, type MemberType,
} from './memberTypeData';

/**
 * EmailDomainScreen — จัดการรายชื่อโดเมนอีเมลของกลุ่ม ปตท.
 *
 * หน้านี้เกิดจากการออกแบบใหม่ที่ให้ยืนยันสิทธิ์พนักงานด้วยโดเมนอีเมลแทนการแนบเอกสาร
 * ผลคือคำขอส่วนใหญ่ไม่ต้องผ่านคิวคน — แต่แลกมาด้วยว่า **รายการโดเมนกลายเป็นจุดเดียว
 * ที่ตัดสินว่าใครได้ราคาพนักงาน** ทุกการแก้ในหน้านี้จึงกระทบสมาชิกจำนวนมากทันที
 *
 * กติกาที่บังคับไว้ในหน้าจอ:
 *   — ปิดใช้งานได้ แต่ลบไม่ได้ (มีสมาชิกผูกอยู่ และ audit ต้องตามรอยได้)
 *   — การปิดโดเมนต้องเลือกให้ชัดว่าจะทำอย่างไรกับสมาชิกที่ยืนยันด้วยโดเมนนั้นไปแล้ว
 *     3 ทางเลือก ไม่มีค่าเริ่มต้น และแสดงจำนวนคนที่กระทบก่อนกดยืนยัน
 *   — เปลี่ยนประเภทสมาชิกที่ผูกกับโดเมน = เปลี่ยนอัตราส่วนลดของทุกคนในโดเมนนั้น
 *     จึงต้องยืนยันพร้อมเหตุผลเช่นกัน
 *
 * ⛔ รายการที่เห็นทั้งหมดยังลงท้าย .example — เป็นค่าสมมติและเป็นตัวขวางการขึ้นระบบจริง
 *    (PROGRESS-REPORT ข้อ 8.1)
 */

type ScreenState = 'ready' | 'loading' | 'error';
type Tab = 'domains' | 'blocked';

const CURRENT_USER = 'สุดารัตน์ ทองดี';

/* ------------------------------------------------------------ ตัวช่วย */

const TypeChip: React.FC<{ type: MemberType }> = ({ type }) => {
  const discount = MEMBER_TYPE_DISCOUNT[type];
  return (
    <span className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--blue-050)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--blue-900)]">
      <Icon name="badge" size={14} />
      {MEMBER_TYPE_LABEL[type]}
      {discount !== undefined && (
        <span className="bo-num text-[var(--ink-700)]">ส่วนลด {discount}%</span>
      )}
    </span>
  );
};

const StateChip: React.FC<{ enabled: boolean }> = ({ enabled }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-[var(--radius-pill)] border px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] ${
      enabled
        ? 'border-[var(--success-border)] bg-[var(--success-bg)] text-[var(--success-text)]'
        : 'border-[var(--line-300)] bg-[var(--surface-100)] text-[var(--ink-700)]'
    }`}
  >
    <Icon name={enabled ? 'check_circle' : 'block'} size={14} filled />
    {enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
  </span>
);

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="flex flex-col gap-[var(--space-05)]">
    <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{label}</span>
    <span className="text-[length:var(--text-form-lg)] leading-[var(--leading-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">{children}</span>
    {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{hint}</span>}
  </div>
);

const inputCls =
  'min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

/* ------------------------------------------------- แผงเพิ่ม/แก้ไขโดเมน */

interface EditState {
  mode: 'add' | 'edit';
  domain: string;
  company: string;
  memberType: MemberType;
  includeSubdomains: boolean;
  original?: EmailDomain;
}

const PTT_TYPES: MemberType[] = ['ptt-employee', 'ptt-group', 'ptt-retired'];

/* ------------------------------------------------------------- หน้าจอ */

export interface EmailDomainScreenProps {
  initialState?: ScreenState;
}

export const EmailDomainScreen: React.FC<EmailDomainScreenProps> = ({ initialState = 'ready' }) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [domains, setDomains] = useState<EmailDomain[]>(DOMAINS);
  const [blocked, setBlocked] = useState(BLOCKED_LOCAL_PARTS);
  const [tab, setTab] = useState<Tab>('domains');

  const [density, setDensity] = useTableDensity();
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | MemberType>('all');
  const [stateFilter, setStateFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [sort, setSort] = useState<SortState | null>({ columnId: 'verifiedUsers', direction: 'desc' });
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [detail, setDetail] = useState<EmailDomain | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [disabling, setDisabling] = useState<EmailDomain | null>(null);
  const [policy, setPolicy] = useState<DisablePolicy | null>(null);
  const [retypeTarget, setRetypeTarget] = useState<{ d: EmailDomain; next: MemberType } | null>(null);
  const [newBlocked, setNewBlocked] = useState('');

  /* ---------------------------------------------------------- ตัวกรอง */

  const filtered = useMemo(() => domains.filter((d) => {
    if (typeFilter !== 'all' && d.memberType !== typeFilter) return false;
    if (stateFilter === 'enabled' && !d.enabled) return false;
    if (stateFilter === 'disabled' && d.enabled) return false;
    if (query) {
      const q = query.trim().toLowerCase();
      if (!d.domain.toLowerCase().includes(q) && !d.company.includes(q)) return false;
    }
    return true;
  }), [domains, typeFilter, stateFilter, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (d: EmailDomain): string | number =>
      sort.columnId === 'verifiedUsers' ? d.verifiedUsers
        : sort.columnId === 'activeUsers' ? d.activeUsers
          : sort.columnId === 'memberType' ? MEMBER_TYPE_LABEL[d.memberType]
            : sort.columnId === 'company' ? d.company
              : d.domain;
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const rows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const totals = useMemo(() => ({
    enabled: domains.filter((d) => d.enabled).length,
    disabled: domains.filter((d) => !d.enabled).length,
    verified: domains.reduce((s, d) => s + d.verifiedUsers, 0),
    active: domains.reduce((s, d) => s + d.activeUsers, 0),
  }), [domains]);

  /* ---------------------------------------------------------- คอลัมน์ */

  const columns: Array<Column<EmailDomain>> = useMemo(() => [
    {
      id: 'domain', header: 'โดเมน', width: 208, pinned: true, required: true, sortable: true,
      value: (d) => d.domain,
      cell: (d) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-num text-[length:var(--text-table)]" style={{ textAlign: 'left' }}>
            @{d.domain}
            {d.includeSubdomains && <span className="ml-1 text-[var(--ink-600)]">+ โดเมนย่อย</span>}
          </span>
          <span className="bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]">{d.company}</span>
        </span>
      ),
    },
    { id: 'memberType', header: 'ประเภทสมาชิกที่ผูก', width: 234, sortable: true, value: (d) => MEMBER_TYPE_LABEL[d.memberType], cell: (d) => <TypeChip type={d.memberType} /> },
    { id: 'enabled', header: 'สถานะ', width: 120, value: (d) => (d.enabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'), cell: (d) => <StateChip enabled={d.enabled} /> },
    {
      id: 'verifiedUsers', header: 'ผู้ใช้ที่ยืนยันด้วยโดเมนนี้', width: 176, numeric: true, sortable: true,
      value: (d) => d.verifiedUsers,
      cell: (d) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-num">{d.verifiedUsers.toLocaleString('th-TH')} คน</span>
          <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
            ยังไม่หมดอายุ {d.activeUsers.toLocaleString('th-TH')}
          </span>
        </span>
      ),
    },
    { id: 'addedAt', header: 'เพิ่มเมื่อ', width: 116, value: (d) => d.addedAt },
    { id: 'addedBy', header: 'เพิ่มโดย', width: 132, value: (d) => d.addedBy },
    {
      id: 'note', header: 'หมายเหตุ', width: 300,
      value: (d) => d.disabledReason ?? '',
      cell: (d) => (d.disabledReason
        ? <span className="bo-truncate text-[var(--ink-700)]">{d.disabledReason}</span>
        : <span className="text-[var(--ink-400)]">—</span>),
    },
    {
      id: 'actions', header: 'จัดการ', width: 168, align: 'right',
      value: () => '',
      cell: (d) => (
        <span className="flex items-center justify-end gap-[var(--space-1)]">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEdit({ mode: 'edit', domain: d.domain, company: d.company, memberType: d.memberType, includeSubdomains: d.includeSubdomains, original: d }); }}
            className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--blue-700)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            แก้ไข
          </button>
          {d.enabled ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setDisabling(d); setPolicy(null); }}
              className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--error-text)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
            >
              ปิดใช้งาน
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setDomains((prev) => prev.map((x) => (x.id === d.id ? { ...x, enabled: true, disabledReason: undefined, disabledAt: undefined, disabledPolicy: undefined } : x)));
                toast.show({ tone: 'success', message: `เปิดใช้งาน @${d.domain} อีกครั้ง — บันทึกใน Audit Log` });
              }}
              className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-table-sm)] font-[var(--weight-medium)] text-[var(--success-text)] underline underline-offset-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
            >
              เปิดใช้งาน
            </button>
          )}
        </span>
      ),
    },
  ], [toast]);

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (typeFilter !== 'all') out.push({ id: 'type', field: 'ประเภทสมาชิก', display: MEMBER_TYPE_LABEL[typeFilter] });
    if (stateFilter !== 'all') out.push({ id: 'state', field: 'สถานะ', display: stateFilter === 'enabled' ? 'เปิดใช้งาน' : 'ปิดใช้งาน' });
    return out;
  }, [typeFilter, stateFilter]);

  const clearAll = () => { setTypeFilter('all'); setStateFilter('all'); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: visible.filter((id) => id !== 'actions'), filename: exportFilename('รายชื่อโดเมนอีเมล'), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} โดเมนแล้ว — บันทึกใน Audit Log` });
  };

  /* ------------------------------------------------------- การบันทึก */

  const saveEdit = () => {
    if (!edit) return;
    const clean = edit.domain.trim().replace(/^@/, '').toLowerCase();
    if (!clean) return;

    if (edit.mode === 'add') {
      if (domains.some((d) => d.domain === clean)) {
        toast.show({ tone: 'error', message: `@${clean} มีอยู่ในรายการแล้ว` });
        return;
      }
      setDomains((prev) => [...prev, {
        id: 'd' + Date.now(), domain: clean, company: edit.company.trim() || '—',
        memberType: edit.memberType, enabled: true, includeSubdomains: edit.includeSubdomains,
        verifiedUsers: 0, activeUsers: 0,
        addedAt: '2569-08-21', addedBy: CURRENT_USER,
      }]);
      setEdit(null);
      toast.show({ tone: 'success', message: `เพิ่ม @${clean} แล้ว — พนักงานที่ใช้โดเมนนี้ยืนยันสิทธิ์เองได้ทันที` });
      return;
    }

    const original = edit.original!;
    /* เปลี่ยนประเภทสมาชิก = เปลี่ยนอัตราส่วนลดของทุกคนในโดเมน ต้องยืนยันพร้อมเหตุผล */
    if (edit.memberType !== original.memberType && original.verifiedUsers > 0) {
      setRetypeTarget({ d: original, next: edit.memberType });
      return;
    }
    setDomains((prev) => prev.map((d) => (d.id === original.id
      ? { ...d, domain: clean, company: edit.company.trim() || d.company, memberType: edit.memberType, includeSubdomains: edit.includeSubdomains }
      : d)));
    setEdit(null);
    toast.show({ tone: 'success', message: `บันทึกการแก้ไข @${clean} แล้ว — บันทึกใน Audit Log` });
  };

  /* ------------------------------------------------------------ ตาราง */

  const emptyState = activeFilters.length || query
    ? <EmptyState kind="no-results" title="ไม่พบโดเมนที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
    : <EmptyState kind="no-data" title="ยังไม่มีโดเมนในรายการ" description="เพิ่มโดเมนของบริษัทในเครือเพื่อให้พนักงานยืนยันสิทธิ์ได้เองโดยไม่ต้องแนบเอกสาร" action={{ label: 'เพิ่มโดเมน', onClick: () => setEdit({ mode: 'add', domain: '', company: '', memberType: 'ptt-group', includeSubdomains: false }) }} />;

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="โดเมนอีเมลของกลุ่ม ปตท."
        count={domains.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหาโดเมนหรือชื่อบริษัท" width={300} />}
        end={
          <>
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="email-domains" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
            <button
              type="button"
              onClick={() => setEdit({ mode: 'add', domain: '', company: '', memberType: 'ptt-group', includeSubdomains: false })}
              className="inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name="add" size={18} />เพิ่มโดเมน
            </button>
          </>
        }
      />

      {/* ⛔ รายการยังเป็นค่าสมมติ — ต้องเห็นทุกครั้งที่เปิดหน้านี้ */}
      <div role="alert" className="flex items-start gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--error-bg)] px-[var(--space-4)] py-[var(--space-2)]">
        <Icon name="report" size={19} filled className="text-[var(--error-text)]" />
        <p className="max-w-[104ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
          <strong className="text-[var(--error-text)]">รายการนี้ยังเป็นค่าสมมติทั้งหมด</strong> — ทุกโดเมนลงท้าย{' '}
          <code className="bo-num rounded-[var(--radius-xs)] bg-white/70 px-1">.example</code>{' '}
          พนักงานจริงจึงยืนยันสิทธิ์ไม่ได้ ต้องได้รายชื่อโดเมนจริงของบริษัทแม่และบริษัทในเครือทั้งหมด
          พร้อมประเภทสมาชิกที่ผูกกับแต่ละโดเมน และผู้รับผิดชอบปรับปรุงรายการเมื่อมีบริษัทเข้าออกจากเครือ
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-4)] border-b border-[var(--line-200)] bg-[var(--blue-050)] px-[var(--space-4)] py-[var(--space-2)]">
        {[
          ['โดเมนที่เปิดใช้งาน', totals.enabled.toLocaleString('th-TH'), 'cloud_done'],
          ['ปิดใช้งาน', totals.disabled.toLocaleString('th-TH'), 'block'],
          ['ผู้ใช้ที่ยืนยันด้วยโดเมน', totals.verified.toLocaleString('th-TH'), 'verified_user'],
          ['สิทธิ์ที่ยังไม่หมดอายุ', totals.active.toLocaleString('th-TH'), 'schedule'],
        ].map(([label, value, icon]) => (
          <span key={label} className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            <Icon name={icon!} size={18} className="text-[var(--blue-700)]" />
            {label}
            <strong className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{value}</strong>
          </span>
        ))}
      </div>

      <div role="tablist" aria-label="มุมมอง" className="flex items-end gap-[var(--space-1)] border-b border-[var(--line-200)] px-[var(--space-4)]">
        {([['domains', 'รายชื่อโดเมน', 'dns', domains.length], ['blocked', 'อีเมลทั่วไปที่ถูกบล็อก', 'block', blocked.length]] as Array<[Tab, string, string, number]>).map(([id, label, icon, n]) => {
          const on = tab === id;
          return (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => setTab(id)}
              className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]'
              }`}
            >
              <Icon name={icon} size={18} filled={on} />
              {label}
              <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${on ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {tab === 'domains' ? (
        <>
          <FilterBar
            active={activeFilters}
            onRemove={(id) => { if (id === 'type') setTypeFilter('all'); if (id === 'state') setStateFilter('all'); setPage(1); }}
            onClearAll={clearAll}
            currentFilters={{ typeFilter, stateFilter }}
            persistKey="email-domains"
            resultCount={sorted.length}
          >
            <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
              ประเภทสมาชิก
              <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as typeof typeFilter); setPage(1); }} className={inputCls}>
                <option value="all">ทั้งหมด</option>
                {PTT_TYPES.map((t) => <option key={t} value={t}>{MEMBER_TYPE_LABEL[t]}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
              สถานะ
              <select value={stateFilter} onChange={(e) => { setStateFilter(e.target.value as typeof stateFilter); setPage(1); }} className={inputCls}>
                <option value="all">ทั้งหมด</option>
                <option value="enabled">เปิดใช้งาน</option>
                <option value="disabled">ปิดใช้งาน</option>
              </select>
            </label>
          </FilterBar>

          <DataTable<EmailDomain>
            columns={columns.filter((c) => visible.includes(c.id))}
            rows={rows}
            rowKey={(d) => d.id}
            caption={`รายชื่อโดเมนอีเมลของกลุ่ม ปตท. — ${sorted.length} รายการ`}
            state={screenState}
            rowCountHint={10}
            errorMessage="โหลดรายชื่อโดเมนไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
            onRetry={() => setScreenState('ready')}
            emptyState={emptyState}
            sort={sort}
            onSortChange={setSort}
            onOpenRow={setDetail}
            density={density}
            persistKey="email-domains"
            isFlagged={(d) => !d.enabled}
            className="min-h-0 flex-1 rounded-none border-0"
          />

          <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[25, 50, 100]} />
        </>
      ) : (
        /* อีเมลทั่วไปที่ถูกบล็อก — กล่องกลางขององค์กร ไม่ผูกกับบุคคล */
        <div className="flex min-h-0 flex-1 flex-col gap-[var(--space-4)] overflow-auto p-[var(--space-6)]">
          <div className="max-w-[80ch]">
            <h3 className="text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
              อีเมลทั่วไปที่ห้ามใช้ยืนยันสิทธิ์
            </h3>
            <p className="mt-[var(--space-1)] text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
              ชื่อกล่องเหล่านี้เป็นกล่องกลางขององค์กร ไม่ผูกกับบุคคลใดคนเดียว
              ถ้าปล่อยให้ใช้ยืนยันได้ คนหลายคนจะได้สิทธิ์จากอีเมลเดียวกัน
              การบล็อกทำที่ส่วนหน้าของอีเมล จึงมีผลกับทุกโดเมนในรายการพร้อมกัน
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-[var(--space-2)]">
            <input
              value={newBlocked}
              onChange={(e) => setNewBlocked(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                const v = newBlocked.trim().toLowerCase().replace(/[@\s]/g, '');
                if (!v || blocked.some((b) => b.value === v)) return;
                setBlocked((prev) => [...prev, { value: v }]);
                setNewBlocked('');
                toast.show({ tone: 'success', message: `บล็อก ${v}@ ทุกโดเมนแล้ว — บันทึกใน Audit Log` });
              }}
              placeholder="เช่น purchasing"
              aria-label="ส่วนหน้าของอีเมลที่จะบล็อก"
              className={`${inputCls} w-[220px]`}
            />
            <span className="text-[length:var(--text-form)] text-[var(--ink-600)]">@ ทุกโดเมน · กด Enter เพื่อเพิ่ม</span>
          </div>

          <ul className="flex flex-wrap gap-[var(--space-2)]">
            {blocked.map((b) => (
              <li key={b.value} className="flex items-center gap-[var(--space-2)] rounded-[var(--radius-pill)] border border-[var(--line-300)] bg-[var(--surface-100)] py-[var(--space-1)] pl-[var(--space-3)] pr-[var(--space-1)]">
                <span className="bo-num text-[length:var(--text-form)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{b.value}@</span>
                {b.note && <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">{b.note}</span>}
                <button
                  type="button"
                  aria-label={`เลิกบล็อก ${b.value}@`}
                  onClick={() => {
                    setBlocked((prev) => prev.filter((x) => x.value !== b.value));
                    toast.show({ tone: 'info', message: `เลิกบล็อก ${b.value}@ แล้ว`, undo: { onUndo: () => setBlocked(BLOCKED_LOCAL_PARTS) } });
                  }}
                  className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full text-[var(--ink-600)] hover:bg-[var(--error-bg)] hover:text-[var(--error-text)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                >
                  <Icon name="close" size={15} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ------------------------------------------------ แผงรายละเอียด */}
      <SideDrawer open={Boolean(detail)} onClose={() => setDetail(null)} title={detail ? '@' + detail.domain : ''} subtitle={detail?.company} width="lg">
        {detail && (
          <div className="flex flex-col gap-[var(--space-4)]">
            <div className="flex flex-wrap items-center gap-[var(--space-2)]">
              <StateChip enabled={detail.enabled} />
              <TypeChip type={detail.memberType} />
            </div>

            <div className="grid grid-cols-2 gap-[var(--space-3)]">
              <Field label="ผู้ใช้ที่ยืนยันด้วยโดเมนนี้" hint={`ยังไม่หมดอายุ ${detail.activeUsers.toLocaleString('th-TH')} คน`}>
                <span className="bo-num" style={{ textAlign: 'left' }}>{detail.verifiedUsers.toLocaleString('th-TH')} คน</span>
              </Field>
              <Field label="โดเมนย่อย">{detail.includeSubdomains ? 'นับรวมโดเมนย่อยทั้งหมด' : 'ไม่นับโดเมนย่อย'}</Field>
              <Field label="เพิ่มเมื่อ" hint={'โดย ' + detail.addedBy}>{detail.addedAt}</Field>
              <Field label="อัตราส่วนลดของประเภทนี้">
                {MEMBER_TYPE_DISCOUNT[detail.memberType] !== undefined ? `${MEMBER_TYPE_DISCOUNT[detail.memberType]}%` : 'ยังไม่กำหนด'}
              </Field>
            </div>

            {!detail.enabled && detail.disabledReason && (
              <div className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-300)] bg-[var(--surface-100)] p-[var(--space-3)]">
                <p className="text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-900)]">ปิดใช้งานเมื่อ {detail.disabledAt}</p>
                <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">{detail.disabledReason}</p>
                {detail.disabledPolicy && (
                  <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                    <span className="text-[var(--ink-600)]">สมาชิกเดิม: </span>
                    {DISABLE_POLICY_LABEL[detail.disabledPolicy]}
                  </p>
                )}
              </div>
            )}

            <section>
              <h3 className="mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ประวัติการแก้ไข</h3>
              <AuditTrail entries={domainAudit(detail) as never} />
            </section>
          </div>
        )}
      </SideDrawer>

      {/* ------------------------------------------------ เพิ่ม/แก้ไข */}
      <SideDrawer
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title={edit?.mode === 'add' ? 'เพิ่มโดเมน' : 'แก้ไขโดเมน'}
        subtitle={edit?.mode === 'edit' ? '@' + (edit?.original?.domain ?? '') : 'พนักงานที่ใช้โดเมนนี้จะยืนยันสิทธิ์ได้เองทันที'}
      >
        {edit && (
          <div className="flex flex-col gap-[var(--space-4)]">
            <label className="flex flex-col gap-[var(--space-1)]">
              <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">โดเมน <span className="text-[var(--error-text)]">จำเป็น</span></span>
              <span className="flex items-center gap-[var(--space-1)]">
                <span className="text-[length:var(--text-form-lg)] text-[var(--ink-600)]">@</span>
                <input
                  data-autofocus
                  value={edit.domain}
                  onChange={(e) => setEdit({ ...edit, domain: e.target.value })}
                  placeholder="pttor.co.th"
                  className={`${inputCls} bo-num flex-1`}
                />
              </span>
            </label>

            <label className="flex flex-col gap-[var(--space-1)]">
              <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">ชื่อบริษัท</span>
              <input value={edit.company} onChange={(e) => setEdit({ ...edit, company: e.target.value })} placeholder="ปตท. น้ำมันและการค้าปลีก" className={inputCls} />
            </label>

            <fieldset className="flex flex-col gap-[var(--space-1)]">
              <legend className="mb-[var(--space-1)] text-[length:var(--text-form)] text-[var(--ink-700)]">
                ประเภทสมาชิกที่ผูก <span className="text-[var(--ink-600)]">— กำหนดอัตราส่วนลดที่พนักงานโดเมนนี้จะได้</span>
              </legend>
              {PTT_TYPES.map((t) => (
                <label key={t} className="flex min-h-[var(--hit-control)] cursor-pointer items-center gap-[var(--space-2)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form-lg)] text-[var(--ink-900)] hover:bg-[var(--blue-050)]">
                  <input type="radio" name="member-type" checked={edit.memberType === t} onChange={() => setEdit({ ...edit, memberType: t })} className="h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]" />
                  {MEMBER_TYPE_LABEL[t]}
                  {MEMBER_TYPE_DISCOUNT[t] !== undefined && <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ส่วนลด {MEMBER_TYPE_DISCOUNT[t]}%</span>}
                  {t === 'ptt-retired' && <span className="text-[length:var(--text-table-sm)] text-[var(--warning-text)]">ยังไม่มีนโยบาย</span>}
                </label>
              ))}
            </fieldset>

            <label className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] bg-[var(--blue-050)] p-[var(--space-25)]">
              <input type="checkbox" checked={edit.includeSubdomains} onChange={(e) => setEdit({ ...edit, includeSubdomains: e.target.checked })} className="mt-[3px] h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]" />
              <span>
                <span className="block text-[length:var(--text-form-lg)] text-[var(--ink-900)]">นับโดเมนย่อยเข้าด้วย</span>
                <span className="block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                  เช่น <span className="bo-num">hr.{edit.domain || 'pttor.co.th'}</span> จะยืนยันสิทธิ์ได้ด้วย —
                  เปิดเมื่อบริษัทใช้โดเมนย่อยแยกตามฝ่าย
                </span>
              </span>
            </label>

            {edit.mode === 'edit' && edit.memberType !== edit.original?.memberType && (edit.original?.verifiedUsers ?? 0) > 0 && (
              <div role="alert" className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-3)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--warning-text)]">
                <Icon name="warning" size={18} filled />
                <span>
                  การเปลี่ยนประเภทสมาชิกจะเปลี่ยนอัตราส่วนลดของสมาชิก{' '}
                  <strong className="bo-num">{edit.original!.verifiedUsers.toLocaleString('th-TH')}</strong> คนที่ยืนยันด้วยโดเมนนี้
                  — ต้องยืนยันพร้อมเหตุผลในขั้นถัดไป
                </span>
              </div>
            )}

            <div className="flex items-center gap-[var(--space-2)]">
              <button
                type="button"
                onClick={saveEdit}
                disabled={!edit.domain.trim()}
                className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-5)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--blue-800)] disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                {edit.mode === 'add' ? 'เพิ่มโดเมน' : 'บันทึกการแก้ไข'}
              </button>
              <button
                type="button"
                onClick={() => setEdit(null)}
                className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                ยกเลิก
              </button>
            </div>

            {edit.mode === 'edit' && (
              <p className="border-t border-[var(--line-200)] pt-[var(--space-3)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
                <Icon name="info" size={16} className="mr-1 inline align-[-3px]" />
                โดเมนลบไม่ได้ เพราะมีสมาชิกผูกอยู่และ Audit Log ต้องตามรอยได้ —
                ถ้าไม่ใช้แล้วให้ปิดใช้งานจากรายการ
              </p>
            )}
          </div>
        )}
      </SideDrawer>

      {/* ---------------------------------- ปิดโดเมน: ต้องเลือกนโยบาย */}
      {disabling && (
        <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-[var(--scrim)] p-[var(--space-4)]">
          <div role="dialog" aria-modal="true" aria-labelledby="disable-title" className="flex max-h-[86vh] w-full max-w-[620px] flex-col gap-[var(--space-3)] overflow-auto rounded-[var(--radius-lg)] bg-[var(--surface-0)] p-[var(--space-5)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-modal)]">
            <div className="flex items-start gap-[var(--space-25)]">
              <Icon name="report" size={24} filled className="text-[var(--error-text)]" />
              <h2 id="disable-title" className="text-[length:var(--text-section)] font-[var(--weight-semibold)] leading-[var(--leading-heading)] text-[var(--ink-900)]">
                ปิดใช้งาน @{disabling.domain}
              </h2>
            </div>

            <p className="text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
              หลังปิด พนักงานที่ใช้โดเมนนี้จะยืนยันสิทธิ์ใหม่ด้วยโดเมนไม่ได้
              แต่ยังมีสมาชิก <strong className="bo-num">{disabling.activeUsers.toLocaleString('th-TH')}</strong> คน
              ที่ถือสิทธิ์จากโดเมนนี้อยู่ (จากทั้งหมดที่เคยยืนยัน {disabling.verifiedUsers.toLocaleString('th-TH')} คน)
              — <strong>ต้องเลือกว่าจะทำอย่างไรกับคนกลุ่มนี้</strong>
            </p>

            <fieldset className="flex flex-col gap-[var(--space-2)]">
              <legend className="sr-only">นโยบายกับสมาชิกที่ยืนยันด้วยโดเมนนี้ไปแล้ว</legend>
              {(Object.keys(DISABLE_POLICY_LABEL) as DisablePolicy[]).map((p) => (
                <label
                  key={p}
                  className={`flex cursor-pointer gap-[var(--space-2)] rounded-[var(--radius-md)] border p-[var(--space-3)] ${
                    policy === p ? 'border-[var(--blue-700)] bg-[var(--blue-050)]' : 'border-[var(--line-300)] bg-[var(--surface-0)] hover:bg-[var(--blue-050)]'
                  }`}
                >
                  <input type="radio" name="disable-policy" checked={policy === p} onChange={() => setPolicy(p)} className="mt-[3px] h-4 w-4 accent-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]" />
                  <span>
                    <span className="block text-[length:var(--text-form-lg)] font-[var(--weight-medium)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                      {DISABLE_POLICY_LABEL[p]}
                    </span>
                    <span className="mt-[var(--space-1)] block text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                      ผลที่จะเกิด: {DISABLE_POLICY_EFFECT[p]}
                    </span>
                    {p === 'revoke-now' && (
                      <span className="mt-[var(--space-1)] flex items-center gap-1 text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--error-text)]">
                        <Icon name="priority_high" size={15} />
                        กระทบ {disabling.activeUsers.toLocaleString('th-TH')} คนในวันนี้
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </fieldset>

            <div className="flex items-center justify-end gap-[var(--space-2)] border-t border-[var(--line-200)] pt-[var(--space-3)]">
              <button
                type="button"
                onClick={() => { setDisabling(null); setPolicy(null); }}
                className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-[var(--surface-100)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                disabled={!policy}
                onClick={() => {
                  const before = domains;
                  const d = disabling;
                  setDomains((prev) => prev.map((x) => (x.id === d.id
                    ? { ...x, enabled: false, disabledAt: '2569-08-21', disabledPolicy: policy!, disabledReason: 'ปิดใช้งานโดยเจ้าหน้าที่' }
                    : x)));
                  setDisabling(null);
                  setPolicy(null);
                  toast.show({
                    tone: 'success',
                    message: `ปิด @${d.domain} แล้ว — ${DISABLE_POLICY_LABEL[policy!]}`,
                    undo: { onUndo: () => setDomains(before), seconds: 12 },
                  });
                }}
                className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] bg-[var(--error-solid)] px-[var(--space-5)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--error-text)] disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                ปิดใช้งานโดเมน
              </button>
              {!policy && <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">เลือกนโยบายก่อน</span>}
            </div>
          </div>
        </div>
      )}

      {/* -------------------------- เปลี่ยนประเภทสมาชิกที่ผูกกับโดเมน */}
      <ConfirmDialog
        open={Boolean(retypeTarget)}
        tone="danger"
        title="เปลี่ยนประเภทสมาชิกที่ผูกกับโดเมน"
        description={retypeTarget ? (
          <>
            <p className="mb-[var(--space-2)]">
              เปลี่ยน <span className="bo-num">@{retypeTarget.d.domain}</span> จาก{' '}
              <strong>{MEMBER_TYPE_LABEL[retypeTarget.d.memberType]}</strong> เป็น{' '}
              <strong>{MEMBER_TYPE_LABEL[retypeTarget.next]}</strong>
            </p>
            <p>
              สมาชิก <strong className="bo-num">{retypeTarget.d.verifiedUsers.toLocaleString('th-TH')}</strong> คน
              ที่ยืนยันด้วยโดเมนนี้จะได้อัตราส่วนลดใหม่
              {MEMBER_TYPE_DISCOUNT[retypeTarget.d.memberType] !== undefined && MEMBER_TYPE_DISCOUNT[retypeTarget.next] !== undefined && (
                <>
                  {' '}จาก <span className="bo-num">{MEMBER_TYPE_DISCOUNT[retypeTarget.d.memberType]}%</span>
                  {' '}เป็น <span className="bo-num">{MEMBER_TYPE_DISCOUNT[retypeTarget.next]}%</span>
                </>
              )}
              {' '}ทันทีในการสั่งซื้อครั้งถัดไป
            </p>
          </>
        ) : ''}
        confirmLabel="เปลี่ยนประเภทสมาชิก"
        requireReason
        reasonLabel="เหตุผลที่เปลี่ยน"
        reasonPresets={[
          'บริษัทย้ายสถานะจากบริษัทในเครือเป็นบริษัทแม่',
          'แก้ไขการผูกที่ตั้งค่าผิดตั้งแต่แรก',
          'ปรับตามโครงสร้างกลุ่มบริษัทที่เปลี่ยนไป',
        ]}
        onConfirm={(reason) => {
          if (!retypeTarget || !edit) return;
          const before = domains;
          setDomains((prev) => prev.map((d) => (d.id === retypeTarget.d.id
            ? { ...d, memberType: retypeTarget.next, company: edit.company.trim() || d.company, includeSubdomains: edit.includeSubdomains }
            : d)));
          setRetypeTarget(null);
          setEdit(null);
          toast.show({
            tone: 'success',
            message: `เปลี่ยน @${retypeTarget.d.domain} เป็น ${MEMBER_TYPE_LABEL[retypeTarget.next]} — บันทึกใน Audit Log พร้อมเหตุผล`,
            undo: { onUndo: () => setDomains(before), seconds: 12 },
          });
          void reason;
        }}
        onCancel={() => setRetypeTarget(null)}
      />
    </div>
  );
};

export default EmailDomainScreen;

/* ============================================================================
   รองรับ:
   SRS 3.2    ประเภทสมาชิกที่ต้องอนุมัติ และส่วนลดคนละอัตราของพนักงาน ปตท.
              กับพนักงานกลุ่ม ปตท. — โดเมนหนึ่งผูกกับประเภทเดียว
   SRS 3.5    เงื่อนไขพิเศษกลุ่ม PTT Group — โดเมนคือกลไกพิสูจน์สิทธิ์แทนเอกสาร
   FR-MTYPE-06 เมื่อประเภทเปลี่ยน โครงสร้างราคาและสิทธิ์เปลี่ยนอัตโนมัติ
              จึงต้องยืนยันพร้อมเหตุผลก่อนเปลี่ยนการผูกของโดเมนที่มีสมาชิกอยู่
   FR-ADM-08  คำขอที่ยืนยันด้วยโดเมนได้จะไม่เข้าคิวคน — หน้านี้เป็นตัวกำหนดว่าใครเข้าคิว
   FR-ADM-37  ทุกการเพิ่ม แก้ไข ปิด และเปิดใช้งาน เข้า Audit Log พร้อมค่าเดิม→ค่าใหม่
   FR-ADM-39  การส่งออกรายชื่อโดเมนถูกบันทึกและแจ้งผู้ใช้

   ประเด็นค้าง (PROGRESS-REPORT 8.1 ⛔ ขวางการขึ้นระบบ):
   — รายชื่อโดเมนจริงของบริษัทแม่และบริษัทในเครือทั้งหมด (ตอนนี้ .example ทั้งหมด)
   — ประเภทสมาชิกที่ผูกกับแต่ละโดเมน
   — โดเมนย่อยที่ต้องนับรวมหรือไม่นับ (หน้าจอรองรับทั้งสองแบบแล้ว)
   — ผู้รับผิดชอบปรับปรุงรายการเมื่อมีบริษัทเข้าออกจากเครือ
   ประเด็นค้าง (8.4): ยังไม่มีนโยบายพนักงานเกษียณ — ตัวเลือกนี้มีในหน้าจอแต่กำกับไว้ว่ารอคำตอบ
   ============================================================================ */
